'use client'

import { type ReactNode, useId, useState } from 'react'
import { cn } from '@nwwp/ui-shadcn'
import { CurrencyField, InlineSlider, RetirementAgePicker, SliderField, StepperField } from './fields'
import { fmtDollars } from './format'
import type { CalculatorResults, CalculatorSetters, CalculatorState } from './types'

const toPercent = (rate: number) => Math.round(rate * 1000) / 10
const fromPercent = (pct: number) => pct / 100

const RATE_FIELDS = [
  { key: 'preReturnRate', label: 'Pre-retirement return', min: 1, max: 25 },
  { key: 'postReturnRate', label: 'Post-retirement return', min: 1, max: 25 },
  { key: 'inflationRate', label: 'Inflation rate', min: 0, max: 8 },
  { key: 'raiseRate', label: 'Annual salary increase', min: 0, max: 6 },
] as const

interface InputsPanelProps {
  state: CalculatorState
  setters: CalculatorSetters
  results: CalculatorResults
  /** Contact fields for the locked state, rendered in the same card as the calculator inputs. */
  children?: ReactNode
}

export function InputsPanel({ state, setters, results, children }: InputsPanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const advancedId = useId()

  const contribPct = state.income > 0 ? Math.round(((state.monthlyContribution * 12) / state.income) * 100) : null
  const budgetToday = (state.income * (state.budgetPct / 100)) / 12

  return (
    <section aria-labelledby="ontrack-inputs-heading" className="rounded-3xl border border-smoke bg-white">
      <div className="flex flex-col gap-7 p-5 tablet:p-8">
        <h2 id="ontrack-inputs-heading" className="text-title-xs text-charcoal">
          Your details
        </h2>

        <div className="grid grid-cols-1 gap-7">
          <StepperField
            label="Current age"
            name="current age"
            value={state.age}
            min={18}
            max={80}
            step={1}
            onChange={setters.age}
          />
          <RetirementAgePicker age={state.age} value={state.retirementAge} onChange={setters.retirementAge} />
          <CurrencyField label="Annual pre-tax income" value={state.income} onChange={setters.income} />
          <CurrencyField label="Current retirement savings" value={state.savings} onChange={setters.savings} />
          <CurrencyField
            label="Monthly contribution"
            value={state.monthlyContribution}
            onChange={setters.monthlyContribution}
            hint={contribPct !== null ? `${contribPct}% of annual income` : undefined}
          >
            <InlineSlider
              name="Monthly contribution"
              value={state.monthlyContribution}
              min={0}
              max={10_000}
              step={50}
              onChange={setters.monthlyContribution}
            />
          </CurrencyField>
        </div>

        {children}
      </div>

      <div className="border-t border-smoke">
        <h2 className="text-title-xs text-charcoal">
          <button
            type="button"
            aria-expanded={advancedOpen}
            aria-controls={advancedId}
            onClick={() => setAdvancedOpen((open) => !open)}
            className={cn(
              'flex w-full items-center justify-between gap-4 rounded-b-3xl p-5 text-left tablet:px-8',
              'text-body-bold text-charcoal hover:text-green-dark',
              'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-green'
            )}
          >
            Advanced details
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn(
                'size-5 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                advancedOpen && 'rotate-180'
              )}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </h2>

        <div id={advancedId} hidden={!advancedOpen} className="px-5 pb-8 tablet:px-8">
          <div className="flex flex-col gap-7">
            <h3 className="text-body-xs font-bold text-green-dark uppercase">Retirement spending</h3>
            <SliderField
              label="Monthly budget in retirement"
              name="Monthly budget in retirement"
              value={state.budgetPct}
              min={30}
              max={100}
              step={1}
              onChange={setters.budgetPct}
              display={`${state.budgetPct}%`}
              hint={`${state.budgetPct}% of projected income = ${fmtDollars(results.monthlyBudget)}/mo (${fmtDollars(budgetToday)}/mo in today’s dollars)`}
            />
            <CurrencyField
              label={
                <>
                  Other monthly income <span className="text-body-sm text-neutral-darker">(SS, pension)</span>
                </>
              }
              value={state.otherIncome}
              onChange={setters.otherIncome}
            />
          </div>

          <div className="mt-7 grid grid-cols-1 gap-7 border-t border-smoke pt-7">
            <StepperField
              label="Life expectancy"
              name="life expectancy"
              value={state.lifeExpectancy}
              min={75}
              max={105}
              step={1}
              onChange={setters.lifeExpectancy}
            />
            {RATE_FIELDS.map(({ key, label, min, max }) => (
              <StepperField
                key={key}
                label={label}
                name={label.toLowerCase()}
                value={state[key]}
                min={min}
                max={max}
                step={0.5}
                decimals={1}
                suffix="%"
                toDisplay={toPercent}
                fromDisplay={fromPercent}
                onChange={setters[key]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
